import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  
  const [step, setStep] = useState(1); // 1=form, 2=success
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid link. Please use the link from your verification email.");
    }
  }, [token]);

  const validatePassword = () => {
    if (form.password.length < 6) {
      return "Password must be at least 6 characters long.";
    }
    if (form.password !== form.confirmPassword) {
      return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      // Try venue-onboarding endpoint first, fallback to auth endpoint
      let res;
      try {
        res = await api.post("/venue-onboarding/set-password", {
          token,
          password: form.password,
          confirm_password: form.confirmPassword,
        });
      } catch (venueErr) {
        // If venue endpoint fails, try the general auth endpoint
        if (venueErr.response?.status === 400 || venueErr.response?.status === 404) {
          res = await api.post("/auth/set-password", {
            token,
            password: form.password,
            confirm_password: form.confirmPassword,
          });
        } else {
          throw venueErr;
        }
      }

      if (res.data.success) {
        setEmail(res.data.email || "");
        setStep(2);
        toast.success("Password created successfully!");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to set password. Please try again.";
      toast.error(detail);
      if (detail.includes("Invalid") || detail.includes("expired")) {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate("/login")} className="rounded-full">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full text-center" data-testid="password-success">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">Password Created!</h1>
          <p className="text-muted-foreground mb-2">
            Your password has been set successfully.
          </p>
          {email && (
            <p className="text-sm text-muted-foreground mb-6">
              You can now log in with <strong>{email}</strong>
            </p>
          )}
          <Button 
            onClick={() => navigate("/login")} 
            className="w-full h-12 bg-court-green hover:bg-court-green/90 text-white rounded-full font-heading uppercase tracking-wide"
            data-testid="go-to-login-btn"
          >
            Log In to Your Account
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col">
        <img 
          src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1000&q=80"
          alt="Sports Venue" 
          className="absolute inset-0 w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="relative z-10 mt-auto p-8 pb-12">
          <div className="flex items-center gap-2 mb-6">
            <img src="/setplay-logo.png" alt="SetPlay" className="w-12 h-12 rounded-xl" />
            <span className="font-heading text-white text-2xl font-bold tracking-tight">SETPLAY</span>
          </div>
          <h2 className="font-heading text-4xl text-white font-bold leading-tight mb-3">
            Almost There!
          </h2>
          <p className="text-white/80 text-lg">
            Create your password to secure your Venue Owner account.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/setplay-logo.png" alt="SetPlay" className="w-10 h-10 rounded-lg" />
              <span className="font-heading text-xl font-bold">SETPLAY</span>
            </div>
          </div>

          <div className="w-14 h-14 rounded-2xl bg-court-green/10 flex items-center justify-center mb-6">
            <Lock className="w-7 h-7 text-court-green" />
          </div>

          <h1 className="font-heading text-3xl font-bold mb-2">Create Your Password</h1>
          <p className="text-muted-foreground mb-8">
            Your email has been verified! Now set a secure password for your account.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="set-password-form">
            <div className="relative">
              <Label className="text-xs font-heading uppercase tracking-wider">New Password *</Label>
              <Input 
                data-testid="new-password"
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                placeholder="Min 6 characters"
                className="h-11 rounded-none border-2 pr-10 mt-1"
                autoFocus
              />
              <button 
                type="button" 
                onClick={() => setShowPw(!showPw)} 
                className="absolute right-3 top-8 text-muted-foreground"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="relative">
              <Label className="text-xs font-heading uppercase tracking-wider">Confirm Password *</Label>
              <Input 
                data-testid="confirm-password"
                type={showConfirm ? "text" : "password"}
                value={form.confirmPassword}
                onChange={e => setForm({...form, confirmPassword: e.target.value})}
                placeholder="Re-enter your password"
                className="h-11 rounded-none border-2 pr-10 mt-1"
              />
              <button 
                type="button" 
                onClick={() => setShowConfirm(!showConfirm)} 
                className="absolute right-3 top-8 text-muted-foreground"
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Password Match Indicator */}
            {form.password && form.confirmPassword && (
              <div className={`text-xs flex items-center gap-1 ${
                form.password === form.confirmPassword ? "text-green-600" : "text-red-500"
              }`}>
                {form.password === form.confirmPassword ? (
                  <><CheckCircle2 className="w-3 h-3" /> Passwords match</>
                ) : (
                  <><span className="w-3 h-3">✗</span> Passwords do not match</>
                )}
              </div>
            )}

            <Button 
              data-testid="set-password-submit"
              type="submit" 
              disabled={loading || !form.password || !form.confirmPassword}
              className="w-full h-12 bg-court-green hover:bg-court-green/90 text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Password...
                </>
              ) : (
                <>
                  Create Password
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground mt-6">
            Having trouble? <a href="mailto:support@setplay.net" className="text-court-green hover:underline">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
}
